import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Search, ShieldCheck, Cpu, Eye, Radar, Leaf } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Section, SectionTitle, CTA } from "@/components/site/SiteBits";
import { SERVICES, img } from "@/data/services";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06 } }),
};

const STATS = [
  { v: "3", l: "Operational Hubs" },
  { v: "99.2%", l: "On-Time Delivery" },
  { v: "5+", l: "Cross-Border Corridors" },
  { v: "24/7", l: "Control Tower" },
];

function Reveal({ children, className = "", i = 0 }) {
  return (
    <motion.div variants={reveal} custom={i} initial="hidden" whileInView="show"
      viewport={{ once: true, amount: 0.2 }} className={className}>
      {children}
    </motion.div>
  );
}

export default function Home() {
  const nav = useNavigate();
  const [tn, setTn] = useState("");
  const featured = SERVICES.slice(0, 8);

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[#0B0F17] text-white overflow-hidden">
        <video autoPlay muted loop playsInline preload="auto" poster={img("hero")}
          className="absolute inset-0 w-full h-full object-cover opacity-80" data-testid="hero-video">
          <source src="/hero-truck.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-24 lg:py-36">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="eyebrow text-white/90 bg-primary/80 inline-block px-3 py-1 rounded-full">
            Technology-Enabled Logistics · South Africa
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-5 text-4xl sm:text-6xl font-heading font-extrabold tracking-tight max-w-4xl leading-[1.05] drop-shadow">
            Moving Cargo.<br /><span className="text-primary">Connecting Business.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 text-lg text-white max-w-2xl leading-relaxed drop-shadow">
            Technology-driven freight and logistics solutions connecting businesses across South Africa and the Southern African region.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="bg-primary hover:bg-red-700 h-12 px-7" onClick={() => nav("/track")} data-testid="hero-track">
              <MapPin size={18} className="mr-2" /> Track Shipment
            </Button>
            <Button size="lg" variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 h-12 px-7" onClick={() => nav("/quote")} data-testid="hero-quote">
              Request a Quote <ArrowRight size={18} className="ml-2" />
            </Button>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="mt-10 max-w-xl rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl p-4">
            <p className="text-xs font-mono text-white/80 mb-2">TRACK A SHIPMENT</p>
            <div className="flex gap-2">
              <Input data-testid="hero-track-input" value={tn} onChange={(e) => setTn(e.target.value)}
                placeholder="e.g. EBK-2026-ZA9001" className="bg-white/15 border-white/25 text-white placeholder:text-white/60" />
              <Button className="bg-primary hover:bg-red-700" data-testid="hero-track-go"
                onClick={() => nav(`/track${tn ? `?tn=${tn}` : ""}`)}><Search size={18} /></Button>
            </div>
          </motion.div>
        </div>
        <div className="relative border-t border-white/10 bg-black/25 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {STATS.map((s) => (
              <div key={s.l} className="py-6 px-4 text-center">
                <div className="text-3xl font-heading font-extrabold text-primary">{s.v}</div>
                <div className="text-xs text-white/70 mt-1 font-mono uppercase tracking-wider">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES — image cards */}
      <Section>
        <SectionTitle eyebrow="What We Move" title="End-to-end logistics services"
          subtitle="A complete freight and supply-chain portfolio built for Southern African trade — from a single pallet to full container loads." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((s, i) => (
            <Reveal key={s.slug} i={i}>
              <Link to="/services" data-testid={`home-service-${s.slug}`}
                className="group block relative rounded-2xl overflow-hidden h-56 shadow-sm">
                <img src={img(s.slug)} alt={s.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center mb-2"><s.icon size={18} className="text-white" /></div>
                  <h3 className="font-heading font-bold text-white text-lg leading-tight">{s.title}</h3>
                  <p className="text-white/70 text-xs mt-1 line-clamp-2">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><Link to="/services"><Button variant="outline" data-testid="home-all-services">View all 18 services <ArrowRight size={16} className="ml-2" /></Button></Link></div>
      </Section>

      {/* STORY — alternating image + text (light) */}
      <Section className="!pt-0 space-y-16">
        {[
          { slug: "port-to-warehouse", eyebrow: "Port & Container", title: "From the quayside to your warehouse", body: "We collect your containers directly from the ports of Durban and Cape Town and move them inland, synced to vessel schedules — so your cargo never waits.", to: "/services" },
          { slug: "last-mile", eyebrow: "Last Mile", title: "Delivered to the door, with proof", body: "From distribution centre to the customer's doorstep, every delivery is tracked live and closed out with a digital proof of delivery captured on our driver app.", to: "/track", flip: true },
        ].map((row, i) => (
          <Reveal key={row.slug}>
            <div className={`grid lg:grid-cols-2 gap-10 items-center ${row.flip ? "lg:[direction:rtl]" : ""}`}>
              <div className="rounded-3xl overflow-hidden shadow-lg [direction:ltr]">
                <img src={img(row.slug)} alt={row.title} className="w-full h-[360px] object-cover" />
              </div>
              <div className="[direction:ltr]">
                <p className="eyebrow text-primary">{row.eyebrow}</p>
                <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-extrabold tracking-tight">{row.title}</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">{row.body}</p>
                <Link to={row.to}><Button className="mt-6 bg-primary hover:bg-red-700">Learn more <ArrowRight size={16} className="ml-2" /></Button></Link>
              </div>
            </div>
          </Reveal>
        ))}
      </Section>

      {/* TECHNOLOGY — light */}
      <section className="bg-accent">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow text-primary">Enterprise Technology</p>
            <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-extrabold tracking-tight">Built on an integration-first, ERP-ready platform</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Ebik is engineered as an extensible logistics platform — not an isolated website. Secure APIs connect our operations to your ERP, GPS telematics, payments and customs systems, with a live control tower and AI assistant at the core.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[{ icon: Radar, t: "Live Control Tower" }, { icon: Cpu, t: "AI Logistics Assistant" }, { icon: Eye, t: "Real-Time Visibility" }, { icon: ShieldCheck, t: "Enterprise Security" }].map((f) => (
                <div key={f.t} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3.5">
                  <f.icon size={20} className="text-primary" /> <span className="text-sm font-semibold">{f.t}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal i={1}><div className="rounded-2xl overflow-hidden shadow-lg"><img src={img("supply-chain")} alt="Logistics hub" className="w-full h-[400px] object-cover" /></div></Reveal>
        </div>
      </section>

      {/* CORRIDORS */}
      <Section>
        <SectionTitle center eyebrow="Where We Operate" title="Connecting Southern Africa's trade corridors" />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { city: "Cape Town", role: "Head Office · Western Cape Operations", slug: "container" },
            { city: "Johannesburg", role: "Gauteng Ops · National Distribution Hub", slug: "distribution" },
            { city: "Durban", role: "KwaZulu-Natal Port Operations", slug: "port-to-warehouse" },
          ].map((b, i) => (
            <Reveal key={b.city} i={i}>
              <div className="group relative rounded-2xl overflow-hidden h-72">
                <img src={img(b.slug)} alt={b.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="text-2xl font-heading font-extrabold text-white">{b.city}</h3>
                  <p className="text-sm text-white/80 mt-1">{b.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SUSTAINABILITY */}
      <Section className="!py-0">
        <div className="rounded-3xl bg-accent p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <Leaf className="text-primary" size={36} />
            <h2 className="mt-4 text-3xl font-heading font-extrabold">Sustainable logistics, measured and reported</h2>
            <p className="mt-3 text-muted-foreground">Track carbon emissions per shipment and per kilometre, load utilisation and route efficiency — with a carbon reporting dashboard for corporate customers.</p>
          </div>
          <Link to="/sustainability"><Button size="lg" className="bg-primary hover:bg-red-700" data-testid="home-sustainability">Sustainability report <ArrowRight size={16} className="ml-2" /></Button></Link>
        </div>
      </Section>

      <div className="mt-16"><CTA /></div>
    </div>
  );
}
